import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import MainLayout from './components/Layout/MainLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ClientList from './pages/clients/ClientList';
import ClientForm from './pages/clients/ClientForm';
import ClientDetail from './pages/clients/ClientDetail';
import SavingsAccounts from './pages/accounts/SavingsAccounts';
import AccountForm from './pages/accounts/AccountForm';
import LoanList from './pages/loans/LoanList';
import LoanForm from './pages/loans/LoanForm';
import LoanDetail from './pages/loans/LoanDetail';
import Repayments from './pages/repayments/Repayments';
import Reports from './pages/reports/Reports';
import Analytics from './pages/analytics/Analytics';
import Settings from './pages/tenants/TenantSettings';
import GlobalDashboard from './pages/tenants/GlobalDashboard';
import GlobalTenantSettings from './pages/tenants/GlobalTenantSettings';
import GlobalTenantForm from './pages/tenants/GlobalTenantForm';
import CashRegister from './pages/cash/CashRegister';
import Profile from './pages/Profile';
///////////////////////////////////////////////
import AgencyList from './pages/agencies/AgencyList';
import AgencyForm from './pages/agencies/AgencyForm';
import AgencyDetail from './pages/agencies/AgencyDetail';
import TenantDashboard from './pages/tenants/TenantDashboard';
//////////////////USER ROUTES/////////////////////
import UserList from './pages/users/UserList';
import UserForm from './pages/users/UserForm';
import UserDetail from './pages/users/UserDetail';
import Founders from './pages/founders/Founders';
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Chargement…</div>;
  return user ? children : <Navigate to="/login" replace state={{ from: location }} />;
};

const TenantCreatorRoute = ({ children }) => {
  const { user, hasPermission } = useAuth();
  const roleName = user?.role?.name?.toLowerCase() || '';
  const tenantCreatorOnly = roleName === 'tenant_creator' || (
    hasPermission('create_tenant') &&
    !hasPermission('view_clients') &&
    !hasPermission('system_settings')
  );

  if (tenantCreatorOnly && !window.location.pathname.startsWith('/global')) {
    return <Navigate to="/global/dashboard" replace />;
  }

  return children;
};

const getStoredInstitutionConfig = () => {
  try {
    return JSON.parse(localStorage.getItem('tenant_institution_config') || '{}');
  } catch (error) {
    localStorage.removeItem('tenant_institution_config');
    return {};
  }
};

const InstitutionFeatureRoute = ({ feature, children }) => {
  const { user } = useAuth();
  const config = user?.tenant?.institution_config || getStoredInstitutionConfig();
  if (feature && config?.[feature] === false) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <CurrencyProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <TenantCreatorRoute>
                  <MainLayout />
                </TenantCreatorRoute>
              </PrivateRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            
            {/* Clients Routes */}
            <Route path="clients" element={<ClientList />} />
            <Route path="clients/new" element={<ClientForm />} />
            <Route path="clients/edit/:id" element={<ClientForm />} />
            <Route path="clients/:id" element={<ClientDetail />} />
            
            {/* Accounts Routes */}
            <Route path="accounts" element={<InstitutionFeatureRoute feature="savings_enabled"><SavingsAccounts /></InstitutionFeatureRoute>} />
            <Route path="accounts/new" element={<InstitutionFeatureRoute feature="savings_enabled"><AccountForm /></InstitutionFeatureRoute>} />
            <Route path="accounts/:id" element={<ClientDetail />} />
            
            {/* Loans Routes */}
            <Route path="loans" element={<InstitutionFeatureRoute feature="credit_enabled"><LoanList /></InstitutionFeatureRoute>} />
            <Route path="loans/new" element={<InstitutionFeatureRoute feature="credit_enabled"><LoanForm /></InstitutionFeatureRoute>} />
            <Route path="loans/:id" element={<InstitutionFeatureRoute feature="credit_enabled"><LoanDetail /></InstitutionFeatureRoute>} />
            
            {/* Repayments */}
            <Route path="repayments" element={<Repayments />} />
            <Route path="cash" element={<InstitutionFeatureRoute feature="cash_register_required"><CashRegister /></InstitutionFeatureRoute>} />
            
            {/* Reports */}
            <Route path="reports" element={<Reports />} />
            <Route path="analytics" element={<Analytics />} />
            
            {/* Users */}
            <Route path="users" element={<UserList />} />
            <Route path="users/new" element={<UserForm />} />
            <Route path="users/edit/:id" element={<UserForm />} />
            <Route path="users/:id" element={<UserDetail />} />
            <Route path="founders" element={<Founders />} />
            {/* Settings */}
            <Route path="global" element={<Navigate to="/global/dashboard" replace />} />
            <Route path="global/dashboard" element={<GlobalDashboard />} />
            <Route path="global/settings" element={<GlobalTenantSettings />} />
            <Route path="global/settings/new" element={<GlobalTenantForm />} />
            <Route path="global/settings/:tenantId" element={<GlobalTenantForm />} />
            <Route path="settings" element={<Settings />} />
            <Route path="profile" element={<Profile />} />
            {/* ///////////////////////////////////////////////*/}
            <Route path="agencies" element={<InstitutionFeatureRoute feature="multi_agency_enabled"><AgencyList /></InstitutionFeatureRoute>} />
            <Route path="agencies/new" element={<InstitutionFeatureRoute feature="multi_agency_enabled"><AgencyForm /></InstitutionFeatureRoute>} />
            <Route path="agencies/edit/:id" element={<InstitutionFeatureRoute feature="multi_agency_enabled"><AgencyForm /></InstitutionFeatureRoute>} />
            <Route path="agencies/:id" element={<InstitutionFeatureRoute feature="multi_agency_enabled"><AgencyDetail /></InstitutionFeatureRoute>} />
            <Route path="tenant/dashboard" element={<TenantDashboard />} />
            <Route path="tenant/settings" element={<Navigate to="/settings" replace />} />
          </Route>
        </Routes>
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
